import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { authFetch } from '../auth';

const IMG_API = '/api/images';
const FILE_BASE = '';

const MAX_PX = 1920; // 긴 변 기준 최대 픽셀

/** 원본이 MAX_PX 초과할 때만 비율 유지하며 축소 후 File로 반환 */
function resizeImage(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const { naturalWidth: w, naturalHeight: h } = img;
      if (w <= MAX_PX && h <= MAX_PX) { resolve(file); return; }
      const ratio = Math.min(MAX_PX / w, MAX_PX / h);
      const nw = Math.round(w * ratio);
      const nh = Math.round(h * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = nw; canvas.height = nh;
      canvas.getContext('2d').drawImage(img, 0, 0, nw, nh);
      const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      canvas.toBlob(
        (blob) => resolve(new File([blob], file.name, { type })),
        type, 0.92
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

/**
 * imageKey(저장 경로) → 표시 URL.
 *  - http(s)/슬래시로 시작: 그대로
 *  - 'up/'(백엔드 업로드): 백엔드 /files/ 로 서빙
 *  - 그 외(legacy 정적, 예: images/…): 앞에 / 부착
 */
export const resolveImg = (k) => {
  if (!k) return '';
  if (/^(https?:|\/)/.test(k)) return k;
  if (k.startsWith('up/')) return `${FILE_BASE}/files/${k}`;
  return '/' + k;
};

/**
 * 이미지 선택/업로드/삭제 위젯.
 * props: value(현재 imageKey), onChange(key|null), label
 * 미리보기 + "이미지 변경" → 모달(업로드 + 기존 이미지 그리드 선택/삭제).
 */
export default function ImagePicker({ value, onChange, label = '이미지' }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="img-picker">
      <div className="img-picker-preview">
        {value ? (
          <img src={resolveImg(value)} alt={label} />
        ) : (
          <span className="img-picker-empty">이미지 없음</span>
        )}
      </div>
      <div className="img-picker-actions">
        <button type="button" className="btn-pill sm" onClick={() => setOpen(true)}>
          이미지 변경
        </button>
        {value && (
          <button type="button" className="btn-pill sm danger" onClick={() => onChange(null)}>
            제거
          </button>
        )}
      </div>
      {open && createPortal(
        <ImageModal
          current={value}
          onClose={() => setOpen(false)}
          onSelect={(key) => { onChange(key); setOpen(false); }}
        />,
        document.body
      )}
    </div>
  );
}

function ImageModal({ current, onClose, onSelect }) {
  const [images, setImages] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    authFetch(IMG_API)
      .then((r) => r.json())
      .then((d) => setImages(Array.isArray(d) ? d : []))
      .catch(() => setImages([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const resized = await resizeImage(file);
      const fd = new FormData();
      fd.append('file', resized);
      const res = await authFetch(IMG_API, { method: 'POST', body: fd });
      if (!res.ok) throw new Error(`업로드 실패 (${res.status})`);
      const img = await res.json();
      onSelect(img.key);
    } catch (e) {
      setErr(e.message || '업로드 실패');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('이 이미지를 삭제할까요? 사용 중인 페이지에서 깨질 수 있습니다.')) return;
    try {
      const res = await authFetch(`${IMG_API}/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`삭제 실패 (${res.status})`);
      load();
    } catch (e2) {
      setErr(e2.message || '삭제 실패');
    }
  };

  return (
    <div className="img-modal-backdrop" onClick={onClose}>
      <div className="img-modal" onClick={(e) => e.stopPropagation()}>
        <div className="img-modal-head">
          <h3>이미지 선택</h3>
          <button type="button" className="img-modal-x" onClick={onClose} aria-label="닫기">×</button>
        </div>

        <div className="img-modal-upload">
          <label className="btn-pill sm">
            {busy ? '업로드 중…' : '새 이미지 업로드'}
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              disabled={busy}
              onChange={(e) => upload(e.target.files && e.target.files[0])}
            />
          </label>
          {err && <span className="img-modal-err">{err}</span>}
        </div>

        <div className="img-modal-grid">
          {images === null ? (
            <p className="color-muted">불러오는 중…</p>
          ) : images.length === 0 ? (
            <p className="color-muted">업로드된 이미지가 없습니다.</p>
          ) : (
            images.slice(0, 6).map((img) => (
              <button
                type="button"
                key={img.intNum}
                className={`img-tile${current === img.key ? ' selected' : ''}`}
                onClick={() => onSelect(img.key)}
                title={img.originName}
              >
                <img src={resolveImg(img.key)} alt={img.originName} />
                <span className="img-tile-del" onClick={(e) => remove(img.intNum, e)} title="삭제">×</span>
              </button>
            ))
          )}
          {images !== null && images.length > 6 && (
            <p className="color-muted" style={{ fontSize: '0.8rem', gridColumn: '1/-1', margin: '4px 0 0' }}>
              최근 6개 표시 중 (전체 {images.length}개) — 오래된 이미지는 미사용 정리 후 재업로드하세요.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
